import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, FileText, Edit2, Send, Save, Eye, Download, 
  Plus, Trash2, CheckCircle2, Clock, AlertCircle, X,
  FileSignature, User, Building2, Calendar, DollarSign,
  ChevronDown, ChevronUp, Loader2, Copy, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { toast } from 'sonner';
import HelpIcon from '@/components/HelpIcon';

const API = process.env.REACT_APP_BACKEND_URL;

// Document status colors and labels
const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700', icon: FileText },
  pending_review: { label: 'Pending Review', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  ready_to_send: { label: 'Ready to Send', color: 'bg-blue-100 text-blue-700', icon: Send },
  sent: { label: 'Sent', color: 'bg-purple-100 text-purple-700', icon: Send },
  viewed: { label: 'Viewed', color: 'bg-indigo-100 text-indigo-700', icon: Eye },
  pending_signature: { label: 'Awaiting Signature', color: 'bg-amber-100 text-amber-700', icon: FileSignature },
  signed: { label: 'Signed', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-600', icon: X },
};

// Document category configuration
const CATEGORY_CONFIG = {
  onboarding: { name: 'Client Onboarding', icon: '👋', phase: 'Client Onboarding' },
  pre_design: { name: 'Pre-Design', icon: '📋', phase: 'PreDesign' },
  schematic: { name: 'Schematic Design', icon: '✏️', phase: 'Schematic Design' },
  design_dev: { name: 'Design Development', icon: '📐', phase: 'Design Development' },
  construction_docs: { name: 'Construction Documents', icon: '📄', phase: 'Construction Documents' },
  contract: { name: 'Contract & Bidding', icon: '📝', phase: 'Contract Negotiation' },
  construction_admin: { name: 'Construction Admin', icon: '🏗️', phase: 'Construction Administration' },
  closeout: { name: 'Project Closeout', icon: '✅', phase: 'Project Closeout' },
  general: { name: 'General', icon: '📁', phase: null },
};

// Standard document types with their fields
const DOCUMENT_TYPES = {
  design_agreement: {
    name: 'Design Agreement',
    category: 'pre_design',
    requiresSignature: true,
    description: 'Professional design services agreement between designer and client',
    fields: [
      { key: 'client_name', label: 'Client Name(s)', type: 'text', required: true },
      { key: 'project_address', label: 'Project Address', type: 'text', required: true },
      { key: 'scope_description', label: 'Scope of Work Description', type: 'textarea', required: true },
      { key: 'design_fee', label: 'Design Fee ($)', type: 'number', required: true },
      { key: 'payment_schedule', label: 'Payment Schedule', type: 'textarea', required: false },
      { key: 'estimated_duration', label: 'Estimated Project Duration', type: 'text', required: false },
      { key: 'special_conditions', label: 'Special Conditions', type: 'textarea', required: false },
    ]
  },
  change_order: {
    name: 'Change Order',
    category: 'construction_admin',
    requiresSignature: true,
    description: 'Document changes to the original scope, cost, or schedule',
    fields: [
      { key: 'change_order_number', label: 'Change Order #', type: 'text', required: true },
      { key: 'description', label: 'Description of Change', type: 'textarea', required: true },
      { key: 'reason', label: 'Reason for Change', type: 'textarea', required: true },
      { key: 'cost_impact', label: 'Cost Impact ($)', type: 'number', required: true },
      { key: 'schedule_impact', label: 'Schedule Impact (days)', type: 'number', required: false },
      { key: 'affected_areas', label: 'Affected Areas/Rooms', type: 'text', required: false },
    ]
  },
  invoice: {
    name: 'Invoice',
    category: 'general',
    requiresSignature: false,
    description: 'Billing invoice for services rendered',
    fields: [
      { key: 'invoice_number', label: 'Invoice #', type: 'text', required: true },
      { key: 'invoice_date', label: 'Invoice Date', type: 'date', required: true },
      { key: 'due_date', label: 'Due Date', type: 'date', required: true },
      { key: 'description', label: 'Description of Services', type: 'textarea', required: true },
      { key: 'amount', label: 'Amount ($)', type: 'number', required: true },
      { key: 'payment_terms', label: 'Payment Terms', type: 'text', required: false },
    ]
  },
  construction_estimate: {
    name: 'Construction Estimate',
    category: 'construction_docs',
    requiresSignature: true,
    description: 'Detailed cost estimate for construction work',
    fields: [
      { key: 'estimate_date', label: 'Estimate Date', type: 'date', required: true },
      { key: 'valid_until', label: 'Valid Until', type: 'date', required: true },
      { key: 'scope_summary', label: 'Scope Summary', type: 'textarea', required: true },
      { key: 'total_estimate', label: 'Total Estimate ($)', type: 'number', required: true },
      { key: 'contingency_percent', label: 'Contingency (%)', type: 'number', required: false },
      { key: 'exclusions', label: 'Exclusions', type: 'textarea', required: false },
      { key: 'assumptions', label: 'Assumptions', type: 'textarea', required: false },
    ]
  },
  draw_request: {
    name: 'Draw Request',
    category: 'construction_admin',
    requiresSignature: true,
    description: 'Request for payment based on work completed',
    fields: [
      { key: 'draw_number', label: 'Draw Request #', type: 'text', required: true },
      { key: 'period_from', label: 'Period From', type: 'date', required: true },
      { key: 'period_to', label: 'Period To', type: 'date', required: true },
      { key: 'work_completed', label: 'Work Completed Description', type: 'textarea', required: true },
      { key: 'percent_complete', label: 'Percent Complete (%)', type: 'number', required: true },
      { key: 'amount_requested', label: 'Amount Requested ($)', type: 'number', required: true },
      { key: 'retainage', label: 'Retainage ($)', type: 'number', required: false },
    ]
  },
  lien_waiver: {
    name: 'Lien Waiver',
    category: 'closeout',
    requiresSignature: true,
    description: 'Waiver of lien rights upon payment',
    fields: [
      { key: 'waiver_type', label: 'Waiver Type', type: 'select', options: ['Conditional', 'Unconditional', 'Partial', 'Final'], required: true },
      { key: 'payment_amount', label: 'Payment Amount ($)', type: 'number', required: true },
      { key: 'through_date', label: 'Through Date', type: 'date', required: true },
      { key: 'property_description', label: 'Property Description', type: 'textarea', required: true },
    ]
  },
  selection_approval: {
    name: 'Selection Approval',
    category: 'design_dev',
    requiresSignature: true,
    description: 'Client approval for material/product selections',
    fields: [
      { key: 'room_area', label: 'Room/Area', type: 'text', required: true },
      { key: 'selection_category', label: 'Category', type: 'text', required: true },
      { key: 'product_name', label: 'Product Name', type: 'text', required: true },
      { key: 'manufacturer', label: 'Manufacturer', type: 'text', required: false },
      { key: 'model_number', label: 'Model/SKU', type: 'text', required: false },
      { key: 'finish_color', label: 'Finish/Color', type: 'text', required: false },
      { key: 'unit_cost', label: 'Unit Cost ($)', type: 'number', required: false },
      { key: 'quantity', label: 'Quantity', type: 'number', required: false },
      { key: 'lead_time', label: 'Lead Time', type: 'text', required: false },
      { key: 'notes', label: 'Notes', type: 'textarea', required: false },
    ]
  },
};

export default function DocumentGenerator() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  
  const [project, setProject] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [projectDocuments, setProjectDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [documentFields, setDocumentFields] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Dark mode
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('keyMadeDarkMode');
    return saved === 'true';
  });

  useEffect(() => {
    const handleDarkModeChange = () => {
      const saved = localStorage.getItem('keyMadeDarkMode');
      setDarkMode(saved === 'true');
    };
    window.addEventListener('storage', handleDarkModeChange);
    const interval = setInterval(handleDarkModeChange, 2000);
    return () => {
      window.removeEventListener('storage', handleDarkModeChange);
      clearInterval(interval);
    };
  }, []);

  // Fetch project and documents
  useEffect(() => {
    fetchData();
  }, [projectId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [projectRes, templatesRes, docsRes] = await Promise.all([
        axios.get(`${API}/api/projects/${projectId}`),
        axios.get(`${API}/api/document-templates`),
        axios.get(`${API}/api/projects/${projectId}/documents`),
      ]);
      
      setProject(projectRes.data);
      setTemplates(templatesRes.data);
      setProjectDocuments(docsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load project data');
    } finally {
      setLoading(false);
    }
  };

  // Auto-fill fields from project data
  const getAutoFilledFields = useCallback((docType) => {
    if (!project) return {};
    
    const fields = {};
    const contacts = project.contacts || [];
    const primaryContact = contacts.find(c => c.is_primary) || contacts[0] || {};
    const siteAddr = project.site_address || {};
    
    // Common auto-fills
    fields.client_name = primaryContact.name || project.client_name || '';
    fields.project_address = siteAddr.street 
      ? `${siteAddr.street}, ${siteAddr.city}, ${siteAddr.state} ${siteAddr.zip_code}`
      : '';
    fields.property_description = fields.project_address;
    
    // Date defaults
    fields.invoice_date = new Date().toISOString().split('T')[0];
    fields.estimate_date = new Date().toISOString().split('T')[0];
    fields.due_date = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    fields.valid_until = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    
    // Generate numbers
    const existingDocs = projectDocuments.filter(d => d.name?.includes(docType));
    fields.invoice_number = `INV-${project.project_code}-${String(existingDocs.length + 1).padStart(3, '0')}`;
    fields.change_order_number = `CO-${String(existingDocs.length + 1).padStart(3, '0')}`;
    fields.draw_number = `DR-${String(existingDocs.length + 1).padStart(3, '0')}`;
    
    return fields;
  }, [project, projectDocuments]);

  const handleCreateDocument = (docTypeKey) => {
    const docType = DOCUMENT_TYPES[docTypeKey];
    setSelectedDocType({ key: docTypeKey, ...docType });
    setDocumentFields(getAutoFilledFields(docTypeKey));
    setShowCreateDialog(true);
  };

  const handleEditDocument = (doc) => {
    setSelectedDocument(doc);
    setDocumentFields(doc.field_values || {});
    setShowEditDialog(true);
  };

  const handleSaveDocument = async (isDraft = true) => {
    try {
      setSaving(true);
      
      const docData = {
        project_id: projectId,
        name: selectedDocType?.name || selectedDocument?.name,
        document_type: selectedDocType?.key || selectedDocument?.document_type,
        category: selectedDocType?.category || selectedDocument?.category,
        requires_signature: selectedDocType?.requiresSignature || selectedDocument?.requires_signature,
        field_values: documentFields,
        status: isDraft ? 'draft' : 'ready_to_send',
      };

      if (selectedDocument) {
        // Update existing
        await axios.put(`${API}/api/documents/${selectedDocument.id}`, docData);
        toast.success('Document updated');
      } else {
        // Create new
        await axios.post(`${API}/api/projects/${projectId}/documents/generate`, docData);
        toast.success('Document created');
      }

      setShowCreateDialog(false);
      setShowEditDialog(false);
      setSelectedDocType(null);
      setSelectedDocument(null);
      setDocumentFields({});
      fetchData();
    } catch (error) {
      console.error('Error saving document:', error);
      toast.error('Failed to save document');
    } finally {
      setSaving(false);
    }
  };

  const handleSendDocument = async (doc) => {
    try {
      // First, send to Drawing Room
      await axios.post(`${API}/api/drawing-room/send-document`, {
        project_id: projectId,
        document_type: doc.document_type || 'general',
        document_id: doc.id,
        document_name: doc.name,
        requires_signature: doc.requires_signature,
        requires_approval: !doc.requires_signature, // If doesn't require signature, require approval instead
      });
      
      // Update local document status
      await axios.put(`${API}/api/documents/${doc.id}/status`, { status: 'sent' });
      
      toast.success('Document sent to client\'s Drawing Room', {
        description: 'The client will receive an email notification'
      });
      fetchData();
    } catch (error) {
      console.error('Error sending document:', error);
      if (error.response?.data?.detail?.includes('portal access')) {
        toast.error('No client portal access found', {
          description: 'Please create Drawing Room access for this client first'
        });
      } else {
        toast.error('Failed to send document');
      }
    }
  };

  const renderFieldInput = (field) => {
    const value = documentFields[field.key] || '';
    const inputClass = darkMode ? 'bg-[#2A2A2A] border-[#2A2A2A] text-white' : '';

    switch (field.type) {
      case 'textarea':
        return (
          <Textarea
            value={value}
            onChange={(e) => setDocumentFields({ ...documentFields, [field.key]: e.target.value })}
            className={`min-h-[100px] ${inputClass}`}
            placeholder={`Enter ${field.label.toLowerCase()}...`}
          />
        );
      case 'select':
        return (
          <Select value={value} onValueChange={(v) => setDocumentFields({ ...documentFields, [field.key]: v })}>
            <SelectTrigger className={inputClass}>
              <SelectValue placeholder={`Select ${field.label.toLowerCase()}`} />
            </SelectTrigger>
            <SelectContent>
              {field.options?.map(opt => (
                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case 'date':
        return (
          <Input
            type="date"
            value={value}
            onChange={(e) => setDocumentFields({ ...documentFields, [field.key]: e.target.value })}
            className={inputClass}
          />
        );
      case 'number':
        return (
          <Input
            type="number"
            value={value}
            onChange={(e) => setDocumentFields({ ...documentFields, [field.key]: e.target.value })}
            className={inputClass}
            placeholder="0"
          />
        );
      default:
        return (
          <Input
            type="text"
            value={value}
            onChange={(e) => setDocumentFields({ ...documentFields, [field.key]: e.target.value })}
            className={inputClass}
            placeholder={`Enter ${field.label.toLowerCase()}...`}
          />
        );
    }
  };

  // Group documents by category
  const documentsByCategory = projectDocuments.reduce((acc, doc) => {
    const cat = doc.category || 'general';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(doc);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-gray-900' : 'bg-[#FDFCFB]'}`}>
        <Loader2 className="w-8 h-8 animate-spin text-[#70170F]" />
      </div>
    );
  }

  const bgClass = darkMode ? 'bg-gray-900 text-white' : 'bg-[#FDFCFB]';
  const cardClass = darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : 'bg-white border-[#E5E2DD]';
  const inputClass = darkMode ? 'bg-[#2A2A2A] border-[#2A2A2A] text-white' : '';

  return (
    <div className={`min-h-screen ${bgClass}`}>
      {/* Header */}
      <div className={`border-b ${darkMode ? 'border-[#2A2A2A] bg-[#1A1A1A]' : 'border-[#E5E2DD] bg-white'} px-6 py-4`}>
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(`/projects/${projectId}/hub`)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Project
            </Button>
            <div className="border-l pl-4 flex items-center gap-2">
              <HelpIcon elementId="document-generator" position="inline" />
              <div>
                <h1 className="text-xl font-heading">Document Generator</h1>
                <p className="text-sm text-muted-foreground">{project?.name}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Info Banner */}
        <Card className={`mb-8 border-[#BF7F3F]/50 ${darkMode ? 'bg-[#BF7F3F]/10' : 'bg-[#BF7F3F]/5'}`}>
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-[#BF7F3F] flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">About Document Generation</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Every document can be fully customized before sending. Documents are optional — 
                  you choose which ones to use based on each project&apos;s needs. Changes are auto-saved as drafts.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Create New Documents */}
          <div className="lg:col-span-1">
            <Card className={cardClass}>
              <CardHeader>
                <CardTitle className="text-lg">Create Document</CardTitle>
                <CardDescription>Select a document type to generate</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {Object.entries(DOCUMENT_TYPES).map(([key, docType]) => {
                  const catConfig = CATEGORY_CONFIG[docType.category];
                  return (
                    <Button
                      key={key}
                      variant="outline"
                      className="w-full justify-start text-left h-auto py-3"
                      onClick={() => handleCreateDocument(key)}
                    >
                      <div className="flex items-center gap-3 w-full">
                        <span className="text-lg">{catConfig?.icon || '📄'}</span>
                        <div className="flex-1">
                          <p className="font-medium">{docType.name}</p>
                          <p className="text-xs text-muted-foreground">{catConfig?.name}</p>
                        </div>
                        {docType.requiresSignature && (
                          <Badge variant="outline" className="text-xs">
                            <FileSignature className="w-3 h-3 mr-1" />
                            Sign
                          </Badge>
                        )}
                      </div>
                    </Button>
                  );
                })}
              </CardContent>
              <CardFooter className="border-t pt-4">
                <Button variant="outline" className="w-full" onClick={() => navigate('/documents')}>
                  <Plus className="w-4 h-4 mr-2" />
                  Upload Custom Template
                </Button>
              </CardFooter>
            </Card>
          </div>

          {/* Right Column - Project Documents */}
          <div className="lg:col-span-2">
            <Card className={cardClass}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">Project Documents</CardTitle>
                    <CardDescription>{projectDocuments.length} documents</CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchData}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {projectDocuments.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                    <h3 className="font-medium mb-2">No documents yet</h3>
                    <p className="text-sm text-muted-foreground">
                      Create your first document using the panel on the left
                    </p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {Object.entries(CATEGORY_CONFIG).map(([catKey, catConfig]) => {
                      const catDocs = documentsByCategory[catKey] || [];
                      if (catDocs.length === 0) return null;

                      return (
                        <Collapsible key={catKey} defaultOpen>
                          <CollapsibleTrigger className="w-full">
                            <div className={`flex items-center justify-between p-3 rounded-lg ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-50 hover:bg-gray-100'}`}>
                              <div className="flex items-center gap-2">
                                <span>{catConfig.icon}</span>
                                <span className="font-medium">{catConfig.name}</span>
                                <Badge variant="secondary">{catDocs.length}</Badge>
                              </div>
                              <ChevronDown className="w-4 h-4" />
                            </div>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <div className="space-y-2 mt-2">
                              {catDocs.map(doc => {
                                const statusConfig = STATUS_CONFIG[doc.status] || STATUS_CONFIG.draft;
                                const StatusIcon = statusConfig.icon;
                                
                                return (
                                  <div key={doc.id} className={`flex items-center justify-between p-3 rounded-lg border ${darkMode ? 'border-[#2A2A2A]' : 'border-gray-200'}`}>
                                    <div className="flex items-center gap-3">
                                      <StatusIcon className="w-5 h-5 text-muted-foreground" />
                                      <div>
                                        <p className="font-medium">{doc.name}</p>
                                        <div className="flex items-center gap-2 mt-1">
                                          <Badge className={statusConfig.color}>
                                            {statusConfig.label}
                                          </Badge>
                                          {doc.requires_signature && (
                                            <Badge variant="outline" className="text-xs">
                                              <FileSignature className="w-3 h-3 mr-1" />
                                              Signature
                                            </Badge>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Button variant="ghost" size="sm" onClick={() => handleEditDocument(doc)}>
                                        <Edit2 className="w-4 h-4" />
                                      </Button>
                                      {doc.status === 'draft' || doc.status === 'ready_to_send' ? (
                                        <Button size="sm" className="bg-[#70170F] hover:bg-[#70170F]/90" onClick={() => handleSendDocument(doc)}>
                                          <Send className="w-4 h-4 mr-1" />
                                          Send to Client
                                        </Button>
                                      ) : (
                                        <Button variant="outline" size="sm">
                                          <Download className="w-4 h-4 mr-1" />
                                          Download
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Create Document Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className={`max-w-2xl max-h-[90vh] overflow-y-auto ${darkMode ? 'bg-gray-800 text-white' : ''}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-lg">{CATEGORY_CONFIG[selectedDocType?.category]?.icon}</span>
              Create {selectedDocType?.name}
            </DialogTitle>
            <DialogDescription>
              {selectedDocType?.description}
              <br />
              <span className="text-[#BF7F3F]">All fields are editable. Save as draft to continue later.</span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {selectedDocType?.fields?.map(field => (
              <div key={field.key}>
                <Label className="flex items-center gap-1">
                  {field.label}
                  {field.required && <span className="text-red-500">*</span>}
                </Label>
                <div className="mt-1">
                  {renderFieldInput(field)}
                </div>
              </div>
            ))}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button variant="outline" onClick={() => handleSaveDocument(true)} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save as Draft
            </Button>
            <Button onClick={() => handleSaveDocument(false)} disabled={saving} className="bg-[#70170F] hover:bg-[#70170F]/90 text-white">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Save & Ready to Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Document Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className={`max-w-2xl max-h-[90vh] overflow-y-auto ${darkMode ? 'bg-gray-800 text-white' : ''}`}>
          <DialogHeader>
            <DialogTitle>Edit {selectedDocument?.name}</DialogTitle>
            <DialogDescription>
              Make changes to this document. All changes are saved when you click Save.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {selectedDocument && DOCUMENT_TYPES[selectedDocument.document_type]?.fields?.map(field => (
              <div key={field.key}>
                <Label className="flex items-center gap-1">
                  {field.label}
                  {field.required && <span className="text-red-500">*</span>}
                </Label>
                <div className="mt-1">
                  {renderFieldInput(field)}
                </div>
              </div>
            ))}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={() => handleSaveDocument(true)} disabled={saving} className="bg-[#70170F] hover:bg-[#70170F]/90 text-white">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
